import {
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Row,
  Segmented,
  Typography,
  notification,
} from "antd";
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useApi from "../../utils/useApi";
import { describeError, formatDateTime } from "./status";

const { Text } = Typography;
const CHAVE = ["ai-reports-config"];

const ESFORCOS = [
  { value: "low", label: "Baixo" },
  { value: "medium", label: "Médio" },
  { value: "high", label: "Alto" },
];

const GRUPOS = [
  {
    titulo: "Redação",
    campos: [
      {
        name: "redacao_max_output_tokens",
        label: "Tamanho da resposta",
        unidade: "tokens",
        min: 1000,
        max: 64000,
        step: 1000,
        help: "Limite de cada relatório escrito pelo modelo, raciocínio incluído. Vale para os envios e para as amostras.",
      },
    ],
  },
  {
    titulo: "Planejador",
    campos: [
      {
        name: "planejador_max_output_tokens",
        label: "Tamanho da resposta",
        unidade: "tokens",
        min: 1000,
        max: 64000,
        step: 1000,
        help: "Limite de cada turno do planejador, raciocínio incluído.",
      },
      {
        name: "planejador_max_tool_calls",
        label: "Limite de consultas",
        unidade: "máx.",
        min: 1,
        max: 30,
        help: "Quantas leituras e buscas o planejador pode fazer para montar um plano.",
      },
    ],
  },
  {
    titulo: "Operação",
    campos: [
      {
        name: "max_tentativas",
        label: "Tentativas",
        unidade: "vezes",
        min: 1,
        max: 10,
        help: "Vezes que um relatório volta para a fila quando o modelo falha por instabilidade.",
      },
      {
        name: "provider_timeout",
        label: "Espera pelo modelo",
        unidade: "s",
        min: 10,
        max: 600,
        help: "Tempo máximo de uma chamada ao modelo.",
      },
    ],
  },
  {
    titulo: "Envio por WhatsApp",
    campos: [
      {
        name: "envio_max_caracteres",
        label: "Tamanho da mensagem",
        unidade: "caract.",
        min: 500,
        max: 60000,
        step: 500,
        help: "Mensagem maior que isso sai em partes, quebrada entre filiais.",
      },
      {
        name: "envio_max_tentativas",
        label: "Tentativas de envio",
        unidade: "vezes",
        min: 1,
        max: 10,
        help: "Vezes que uma mensagem é reenviada quando o webhook falha por instabilidade.",
      },
    ],
  },
];

const CAMPOS = ["redacao_effort", "planejador_effort"].concat(
  GRUPOS.flatMap((grupo) => grupo.campos.map((campo) => campo.name))
);

const estiloQuadro = {
  border: "1px solid var(--ouran-card-header-bg)",
  borderRadius: 8,
  padding: "8px 16px 0",
  margin: 0,
  height: "100%",
  minWidth: 0,
};

const estiloTituloQuadro = {
  padding: "0 6px",
  width: "auto",
  margin: 0,
  border: 0,
  fontSize: 14,
  fontWeight: 600,
};

function mudou(valor, original) {
  return (valor ?? null) !== (original ?? null);
}

export default function ConfiguracaoCard() {
  const { getAiReportsConfig, updateAiReportsConfig } = useApi();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const valores = Form.useWatch([], form) || {};

  const { data: original, isFetching } = useQuery({
    queryKey: CHAVE,
    queryFn: getAiReportsConfig,
    staleTime: 0,
  });

  useEffect(() => {
    if (original) form.setFieldsValue(original);
  }, [original, form]);

  const alterados = original
    ? CAMPOS.filter((nome) => mudou(valores[nome], original[nome]))
    : [];

  const salvar = async (campos) => {
    try {
      const resposta = await updateAiReportsConfig(campos);
      queryClient.setQueryData(CHAVE, resposta);
      notification.success({ message: "Configuração salva." });
    } catch (err) {
      notification.error({
        message: "Erro ao salvar a configuração.",
        description: describeError(err, ""),
      });
    }
  };

  const descartar = () => form.setFieldsValue(original);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={salvar}
      requiredMark={false}
      disabled={isFetching && !original}>
      <Card title="Configuração dos relatórios">
        <Row
          gutter={48}
          style={{
            borderBottom: "1px solid var(--ouran-card-header-bg)",
            marginBottom: 20,
          }}>
          <Col flex="none">
            <Form.Item
              name="redacao_effort"
              label="Esforço na redação"
              extra="Vale para os envios e as amostras. Mais esforço deixa o relatório mais lento e caro.">
              <Segmented
                aria-label="Esforço na redação"
                options={[{ value: "", label: "Do modelo" }, ...ESFORCOS]}
              />
            </Form.Item>
          </Col>
          <Col flex="none">
            <Form.Item
              name="planejador_effort"
              label="Esforço do planejador"
              extra="O planejador roda uma vez a cada plano, por isso aceita mais esforço que a redação.">
              <Segmented
                aria-label="Esforço do planejador"
                options={ESFORCOS}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          {GRUPOS.map((grupo) => (
            <Col xs={24} md={12} xl={6} key={grupo.titulo}>
              <fieldset style={estiloQuadro}>
                <legend style={estiloTituloQuadro}>{grupo.titulo}</legend>
                {grupo.campos.map((campo) => (
                  <Form.Item
                    key={campo.name}
                    name={campo.name}
                    label={campo.label}
                    tooltip={campo.help}
                    rules={[{ required: true, message: "Obrigatório." }]}>
                    <InputNumber
                      aria-label={`${grupo.titulo}: ${campo.label}`}
                      min={campo.min}
                      max={campo.max}
                      step={campo.step || 1}
                      status={
                        mudou(valores[campo.name], original?.[campo.name])
                          ? "warning"
                          : ""
                      }
                      addonAfter={
                        <span
                          style={{
                            display: "inline-block",
                            width: 48,
                            textAlign: "center",
                          }}>
                          {campo.unidade}
                        </span>
                      }
                      style={{ width: 170 }}
                    />
                  </Form.Item>
                ))}
              </fieldset>
            </Col>
          ))}
        </Row>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            marginTop: 24,
            paddingTop: 16,
            borderTop: "1px solid var(--ouran-card-header-bg)",
          }}>
          <Button
            type="primary"
            htmlType="submit"
            disabled={alterados.length === 0}>
            Salvar alterações
          </Button>
          <Button onClick={descartar} disabled={alterados.length === 0}>
            Descartar
          </Button>
          {alterados.length > 0 && (
            <Text type="warning">
              {alterados.length === 1
                ? "1 campo alterado"
                : `${alterados.length} campos alterados`}
            </Text>
          )}
          <span style={{ flex: 1 }} />
          {original?.updated_by && (
            <Text type="secondary">
              Alterado em {formatDateTime(original.updated_at)} por{" "}
              {original.updated_by}
            </Text>
          )}
        </div>
      </Card>
    </Form>
  );
}
